import {
	TAKEAWAY_NOTE_MAX,
	TAKEAWAY_NOTE_REQUIRED,
	TAKEAWAY_ORDER_TARGETS,
	type TakeawayOrderNocItem,
	type TakeawayOrderTarget,
	makeTakeawayBulkStatusBody,
	makeTakeawayStatusBody,
} from "@seagames/contracts";
import { Field, Modal, ModalActions, useUiStrings } from "@seagames/ui";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { bulkMoveTakeawayOrders, moveTakeawayOrder } from "../../lib/api/fnb";
import { errorMessage } from "../../lib/api/messages";
import { useFnbFormMessages } from "../../lib/i18n/use-form-schemas";
import { toast } from "../../lib/ui/toast";
import { PackageIcon } from "../icons/sidebar-icons";
import { CheckIcon, ClockIcon, CloseIcon, TrashIcon } from "../transport/icons";
import { joinList } from "./parts";

const needsNote = (to: TakeawayOrderTarget) => (TAKEAWAY_NOTE_REQUIRED as readonly string[]).includes(to);
const ICON: Record<TakeawayOrderTarget, ReactNode> = {
	CONFIRMED: <CheckIcon />,
	COLLECTED: <PackageIcon />,
	REJECTED: <CloseIcon />,
	CANCELLED: <TrashIcon />,
};

type Row = Pick<TakeawayOrderNocItem, "takeaway_order_id" | "ref" | "status" | "packs" | "late" | "next_statuses" | "version">;

/**
 * The host's status moves on selected orders (her "More" sheet + `bulkSheet`):
 *   pick     Confirm ("The kitchen packs it") · Mark collected ("Picked up by
 *            the team") · Reject · Cancel (both need a reason the NOC sees),
 *            each with "x of N" — x = the orders whose `next_statuses` (the
 *            API's own verdict for this viewer) allow it
 *   confirm  "Reject 3 orders · 42 packs", the ones left out and why, the
 *            reason box, and for Confirm a note when some were ordered late
 * ONE `POST /takeaway-orders/bulk/status` (one guarded UPDATE); whatever the
 * API skipped comes back with a reason and is said, never silently dropped.
 * `start` opens straight on a move (the bar's "Confirm N").
 */
export function OrderStatusModal({
	targets,
	start,
	cutoffHours,
	onClose,
	onDone,
}: {
	targets: readonly Row[];
	start?: TakeawayOrderTarget;
	cutoffHours: number;
	onClose: () => void;
	onDone: (updatedIds: readonly string[]) => void;
}) {
	const { t } = useTranslation("fnb");
	const { formatNumber: fmt } = useUiStrings();
	const messages = useFnbFormMessages();
	const [to, setTo] = useState<TakeawayOrderTarget | null>(start ?? null);
	const [note, setNote] = useState("");
	const [error, setError] = useState<string | undefined>();
	const [busy, setBusy] = useState(false);
	const field = useRef<HTMLTextAreaElement>(null);
	useEffect(() => {
		setNote("");
		setError(undefined);
	}, [to]);

	const eligible = (x: TakeawayOrderTarget) => targets.filter((r) => r.next_statuses.includes(x));

	if (to === null)
		return (
			<Modal open onClose={onClose} size="md" title={t("bulk.moreTitle")} description={t("bulk.selected", { count: targets.length, n: fmt(targets.length) })}>
				<ul className="divide-y divide-gms-line rounded-[0.5rem] border border-gms-line">
					{TAKEAWAY_ORDER_TARGETS.map((x) => {
						const n = eligible(x).length;
						return (
							<li key={x}>
								<button
									type="button"
									disabled={n === 0}
									onClick={() => setTo(x)}
									className="flex w-full cursor-pointer items-center gap-[0.75rem] px-[0.75rem] py-[0.625rem] text-left transition-colors hover:bg-gms-soft disabled:cursor-not-allowed disabled:opacity-50"
								>
									<span className="grid size-[2rem] shrink-0 place-items-center rounded-[0.375rem] bg-gms-soft-2 text-gms-ink-2 [&>svg]:size-[1rem]">{ICON[x]}</span>
									<span className="grid min-w-0 flex-1">
										<b className="text-[0.875rem] font-semibold text-gms-navy-ink">{t(`bulk.action.${x}`)}</b>
										<span className="text-[0.75rem] text-gms-ink-3">{t(`bulk.actionHint.${x}`)}</span>
									</span>
									<span className="text-[0.75rem] text-gms-ink-3 tabular-nums">{t("bulk.xOfN", { x: fmt(n), n: fmt(targets.length) })}</span>
								</button>
							</li>
						);
					})}
				</ul>
			</Modal>
		);

	const ok = eligible(to);
	const left = targets.filter((r) => !r.next_statuses.includes(to));
	const packs = ok.reduce((s, r) => s + r.packs, 0);
	const late = to === "CONFIRMED" ? ok.filter((r) => r.late).length : 0;
	const red = to === "REJECTED" || to === "CANCELLED";

	const submit = async () => {
		const parsed = makeTakeawayBulkStatusBody(messages).safeParse({ ids: ok.map((r) => r.takeaway_order_id), to, note });
		if (!parsed.success) {
			setError(parsed.error.issues.find((i) => i.path[0] === "note")?.message ?? parsed.error.issues[0]?.message);
			return;
		}
		setBusy(true);
		try {
			const res = await bulkMoveTakeawayOrders(parsed.data);
			if (res.updated.length > 0) toast.success(t(`bulk.done.${to}`, { count: res.updated.length, n: fmt(res.updated.length) }));
			if (res.skipped.length > 0) {
				const refOf = new Map(targets.map((r) => [r.takeaway_order_id, r.ref]));
				toast.info(
					t("bulk.skippedToast", {
						count: res.skipped.length,
						n: fmt(res.skipped.length),
						list: joinList(res.skipped.slice(0, 5).map((s) => t("bulk.skipItem", { ref: refOf.get(s.takeaway_order_id) ?? "", reason: t(`bulk.skipReason.${s.reason}`) }))),
					}),
				);
			}
			onDone(res.updated);
			onClose();
		} catch (err) {
			toast.error(err, t("bulk.failed"));
		} finally {
			setBusy(false);
		}
	};

	return (
		<Modal
			open
			onClose={onClose}
			size="md"
			title={t(`bulk.confirmTitle.${to}`, { count: ok.length, n: fmt(ok.length) })}
			description={t("bulk.packs", { count: packs, n: fmt(packs) })}
			dismissible={!busy}
			initialFocusRef={needsNote(to) ? field : undefined}
			footer={
				<ModalActions
					start={
						start ? undefined : (
							<button type="button" className="btn btn-ghost btn-sm" onClick={() => setTo(null)} disabled={busy}>
								{t("bulk.back")}
							</button>
						)
					}
				>
					<button type="button" className="btn btn-outline btn-sm" onClick={onClose} disabled={busy}>
						{t("common.cancel")}
					</button>
					<button
						type="submit"
						form="fnb-bulk-form"
						disabled={busy || ok.length === 0}
						className={`btn btn-sm gap-[0.375rem] ${red ? "btn-error" : "border-gms-navy bg-gms-navy text-white hover:bg-gms-navy-lift"}`}
					>
						{busy && <span className="loading loading-spinner loading-xs" />}
						{t(`bulk.verb.${to}`, { n: fmt(ok.length) })}
					</button>
				</ModalActions>
			}
		>
			<form
				id="fnb-bulk-form"
				noValidate
				className="grid gap-[0.75rem]"
				onSubmit={(e) => {
					e.preventDefault();
					void submit();
				}}
			>
				{late > 0 && (
					<p className="flex items-start gap-[0.375rem] rounded-[0.5rem] bg-gms-warn-soft px-[0.75rem] py-[0.5rem] text-[0.8125rem] text-gms-warn">
						<ClockIcon aria-hidden className="mt-[0.125rem] size-[0.875rem] shrink-0" />
						{t("bulk.lateNote", { count: late, n: fmt(late), hours: cutoffHours })}
					</p>
				)}
				{left.length > 0 && (
					<div className="grid gap-[0.25rem]">
						<b className="text-[0.75rem] font-semibold text-gms-ink-2">{t("bulk.leftOut", { count: left.length, n: fmt(left.length) })}</b>
						<ul className="max-h-[8rem] overflow-y-auto text-[0.75rem] text-gms-ink-3">
							{left.map((r) => (
								<li key={r.takeaway_order_id}>{t("bulk.leftItem", { ref: r.ref, status: t(`status.label.${r.status}`) })}</li>
							))}
						</ul>
					</div>
				)}
				{needsNote(to) && (
					<Field label={t("bulk.reason")} htmlFor="fnb-bulk-note" required error={error} hint={t("bulk.reasonHint")}>
						<textarea
							ref={field}
							id="fnb-bulk-note"
							rows={3}
							maxLength={TAKEAWAY_NOTE_MAX}
							value={note}
							onChange={(e) => {
								setNote(e.target.value);
								setError(undefined);
							}}
							className={`textarea textarea-sm w-full ${error ? "textarea-error" : ""}`}
						/>
					</Field>
				)}
				{!needsNote(to) && error && <p className="text-[0.8125rem] text-gms-bad">{error}</p>}
			</form>
		</Modal>
	);
}

/**
 * Cancel one order (her NOC cancel sheet: "Cancel this order · The kitchen
 * will not pack it"). A reason is required — the host sees it. The rule
 * (own order, PENDING / CONFIRMED, meal not started) is the API's:
 * `next_statuses` only decides whether the button shows.
 */
export function CancelOrderModal({ order, onClose }: { order: Pick<TakeawayOrderNocItem, "takeaway_order_id" | "ref" | "version">; onClose: () => void }) {
	const { t } = useTranslation("fnb");
	const messages = useFnbFormMessages();
	const [note, setNote] = useState("");
	const [error, setError] = useState<string | undefined>();
	const [busy, setBusy] = useState(false);
	const field = useRef<HTMLTextAreaElement>(null);

	const submit = async () => {
		const parsed = makeTakeawayStatusBody(messages).safeParse({ to: "CANCELLED", note, version: order.version });
		if (!parsed.success) {
			setError(parsed.error.issues.find((i) => i.path[0] === "note")?.message ?? parsed.error.issues[0]?.message);
			return;
		}
		setBusy(true);
		try {
			await moveTakeawayOrder(order.takeaway_order_id, parsed.data);
			toast.success(t("noc.cancelled", { ref: order.ref }));
			onClose();
		} catch (err) {
			setError(errorMessage(err));
		} finally {
			setBusy(false);
		}
	};

	return (
		<Modal
			open
			onClose={onClose}
			size="sm"
			title={t("noc.cancelTitle")}
			description={t("noc.cancelSub", { ref: order.ref })}
			dismissible={!busy}
			initialFocusRef={field}
			footer={
				<ModalActions>
					<button type="button" className="btn btn-outline btn-sm" onClick={onClose} disabled={busy}>
						{t("noc.back")}
					</button>
					<button type="submit" form="fnb-cancel-form" className="btn btn-error btn-sm" disabled={busy}>
						{busy && <span className="loading loading-spinner loading-xs" />}
						{t("noc.cancelConfirm")}
					</button>
				</ModalActions>
			}
		>
			<form
				id="fnb-cancel-form"
				noValidate
				onSubmit={(e) => {
					e.preventDefault();
					void submit();
				}}
			>
				<Field label={t("bulk.reason")} htmlFor="fnb-cancel-note" required error={error}>
					<textarea
						ref={field}
						id="fnb-cancel-note"
						rows={3}
						maxLength={TAKEAWAY_NOTE_MAX}
						value={note}
						onChange={(e) => {
							setNote(e.target.value);
							setError(undefined);
						}}
						placeholder={t("noc.reasonPlaceholder")}
						className={`textarea textarea-sm w-full ${error ? "textarea-error" : ""}`}
					/>
				</Field>
			</form>
		</Modal>
	);
}
